import React, { useEffect, useMemo, useState } from "react";

import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { trackEvent } from "@needl-ai/common/analytics";
import {
  useBrandingConfig,
  useIsICRAResearchAssistBranding,
} from "@needl-ai/common/branding";
import { useFetchUserInfo } from "@needl-ai/common/hooks/useFetchUserInfo";
import { BudgetInputCap } from "@needl-ai/common/usage/components/BudgetInputCap";
import { BudgetBanner } from "@needl-ai/common/usage/components/BudgetTopBanner";
import { useBudgetRestricted } from "@needl-ai/common/usage/hooks/useBudgetRestricted";
import { useGetPathPrefix } from "@needl-ai/common/workspace";

import ChatInputSection from "@/components/main/ChatInputSection";
import Sidebar from "@/components/Sidebar";
import { DragDropOverlay } from "@/components/ui/DragDropOverlay";
import TrialExpirationAlert from "@/components/ui/TrialExpirationAlert";
import {
  COLOR_CLASSES,
  getCategoryPresentation,
} from "@/constants/categoryPresentation";
import { useFileUploadContext } from "@/contexts/FileUploadContext";
import { useWelcomeContent } from "@/contexts/UIContext";
import { useChatContext } from "@/contexts/useChatContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAskNeedlUpload } from "@/hooks/useAskNeedlUpload";
import { useAttachmentBlocker } from "@/hooks/useAttachmentBlocker";
import { usePrepopulateSession } from "@/hooks/useCreateUpdateSession";
import { useFetchAskNeedlTools } from "@/hooks/useFetchAskNeedlTools";
import { useFetchSuggestedPrompts } from "@/hooks/useFetchSuggestedPrompts";
import { shouldDisableFileUpload } from "@/utils/attachmentUtils";
import { AIDisclaimer } from "@/vendor/AIDisclaimer";
import { SidebarInset, useSidebar } from "@/vendor/AppSidebar";

const Landing = () => {
  const [input, setInput] = useState("");
  const { isLoading: isUserLoading, data: userData } = useFetchUserInfo();
  const { isRestricted: isBudgetRestricted } = useBudgetRestricted();
  const { showWelcomeContent } = useWelcomeContent();
  const { state: sidebarState, setOpen: setSidebarOpen } = useSidebar();
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string | null>(
    null
  );
  const prepolateSessionData = usePrepopulateSession();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const pathPrefix = useGetPathPrefix();
  const { data: toolsData } = useFetchAskNeedlTools();
  const { setTools, tools, initializeChatState } = useChatContext();
  const { hasIncompleteAttachments } = useAttachmentBlocker();
  const { handleDrop, handleDragOver, handleDragLeave, isDragging } =
    useAskNeedlUpload();
  const { state } = useFileUploadContext();
  const isFileUploadDisabled = shouldDisableFileUpload(
    state.attachments,
    state.attachment_feed_id
  );
  const brandingConfig = useBrandingConfig();
  const isICRAResearchAssist = useIsICRAResearchAssistBranding();
  const { data: promptCategories = [] } = useFetchSuggestedPrompts();
  const isFlatLayout =
    promptCategories.length === 1 && !promptCategories[0].label;
  const flatPrompts = isFlatLayout ? promptCategories[0].prompts : [];
  const selectedCategory = promptCategories.find(
    (c) => c.key === selectedCategoryKey
  );
  const showSelectedPrompts =
    !isFlatLayout &&
    !!selectedCategory &&
    selectedCategory.type !== "meta" &&
    selectedCategory.prompts.length > 0;

  const isSidebarOpen = sidebarState === "expanded";

  const scLandingPlaceholders = brandingConfig.scLandingPlaceholders;
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  useEffect(() => {
    if (!scLandingPlaceholders?.length) return;
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % scLandingPlaceholders.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [scLandingPlaceholders]);

  const handleStartChat = React.useCallback(
    (prompt: string) => {
      if (prompt.trim()) {
        initializeChatState();

        const sessionId = uuidv4();
        prepolateSessionData(sessionId, {
          id: sessionId,
          title: "Preliminary Session",
          state: {
            messages: [],
            tools: tools ?? [],
            attachment_feed_id: state.attachment_feed_id,
          },
          askneedl_type: "SUPERCHARGING_ASKNEEDL",
          updated_at: new Date().toISOString(),
        });

        navigate(`${pathPrefix}/c/${sessionId}`, {
          state: {
            initialPrompt: prompt.trim(),
            tools: tools,
          },
          replace: true,
        });
      }
    },
    [
      navigate,
      prepolateSessionData,
      pathPrefix,
      state.attachment_feed_id,
      tools,
      initializeChatState,
    ]
  );

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Escape key to clear input or close sidebar
      if (event.key === "Escape") {
        if (input.trim()) {
          setInput("");
        } else if (isSidebarOpen && isMobile) {
          setSidebarOpen(false);
        }
      }
      // Ctrl/Cmd + Enter to start chat quickly
      if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
        if (input.trim()) {
          handleStartChat(input.trim());
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [input, isSidebarOpen, isMobile, handleStartChat, setSidebarOpen]);

  // Close sidebar by default on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
    // For desktop, keep whatever state it currently has (don't force to true)

    // Cleanup function to remove overflow-hidden class
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isMobile, setSidebarOpen]);

  const handleSendMessage = () => {
    if (input.trim()) {
      handleStartChat(input.trim());
    }
  };

  React.useEffect(() => {
    if (toolsData && toolsData.length > 0) {
      setTools(toolsData);
    }
  }, [toolsData, setTools]);

  const greeting = useMemo(
    () => getGreetings({ username: userData?.username }),
    [userData?.username]
  );

  if (isUserLoading) {
    return null;
  }

  return (
    <div className="flex min-h-screen w-full">
      {/* Drag and Drop Overlay */}
      <DragDropOverlay
        isDragging={isDragging}
        isFileUploadDisabled={isFileUploadDisabled}
        isLandingPage={true}
      />
      <Sidebar />
      {/* Mobile overlay */}
      {isSidebarOpen && isMobile && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-60 md:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <SidebarInset>
        <BudgetBanner message="New queries are paused until your admin increases the limit." />
        {/* Main content area with header and landing content */}
        <div
          onDrop={isFileUploadDisabled ? undefined : handleDrop}
          onDragOver={isFileUploadDisabled ? undefined : handleDragOver}
          onDragLeave={isFileUploadDisabled ? undefined : handleDragLeave}
          className="h-full"
        >
          {/* Main Content - now with proper overflow handling */}
          <div
            className={`
          flex-1 min-w-0 h-svh transition-all duration-300 overflow-y-auto
          ${isMobile && isSidebarOpen ? "opacity-50" : "opacity-100"}
        `}
          >
            <div className="flex flex-col min-h-full">
              {/* Welcome Section */}
              <div className="flex-1 flex flex-col items-center justify-start mt-[25vh] px-6 pb-2 min-h-0">
                <div className="text-center max-w-3xl mx-auto w-full">
                  <h2 className="text-4xl font-normal mb-4">
                    {isICRAResearchAssist || showWelcomeContent ? (
                      <>
                        Welcome to{" "}
                        <span className="text-primary font-semibold">
                          {brandingConfig.scAppName}
                        </span>
                      </>
                    ) : (
                      greeting
                    )}
                  </h2>
                  {!isICRAResearchAssist && (
                    <p className="text-sm text-gray-600 dark:text-neutral-400 mb-8">
                      {brandingConfig.scLandingSubheading ??
                        "Your private AI assistant that answers questions using your data and trusted sources"}
                    </p>
                  )}
                  {isICRAResearchAssist && userData?.trial_expires_at && (
                    <TrialExpirationAlert
                      trialExpiresAt={userData.trial_expires_at}
                    />
                  )}
                  <div className="w-full max-w-3xl mx-auto mt-12">
                    <ChatInputSection
                      input={input}
                      onInputChange={setInput}
                      onSend={handleSendMessage}
                      onStop={() => {}}
                      disabled={isBudgetRestricted}
                      cap={isBudgetRestricted ? <BudgetInputCap /> : undefined}
                      placeholder={
                        isBudgetRestricted
                          ? "Queries are paused…"
                          : isICRAResearchAssist
                            ? "Start a conversation..."
                            : scLandingPlaceholders?.length
                              ? scLandingPlaceholders[placeholderIndex]
                              : "Ask a question about your data or connected sources…"
                      }
                    />
                  </div>

                  {/* Quick Action Buttons */}
                  {isFlatLayout ? (
                    <div className="flex flex-col items-center mt-6 gap-2 w-full max-w-fit mx-auto">
                      {flatPrompts.map((q, i) => (
                        <button
                          key={q}
                          onClick={() => setInput(q)}
                          disabled={hasIncompleteAttachments}
                          className={`w-full text-left px-6 py-3 text-sm
                            bg-transparent border-none shadow-none
                            text-gray-500 dark:text-neutral-300
                            ${
                              hasIncompleteAttachments
                                ? "opacity-50 cursor-not-allowed"
                                : "cursor-pointer"
                            }
                            transition-colors
                            hover:text-gray-900 dark:hover:text-white
                            hover:no-underline`}
                          data-testid={`flat-suggested-prompt-${i}`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  ) : promptCategories.length === 0 ? null : (
                    <div className="flex justify-center gap-2 mt-6">
                      {promptCategories.map((category, index) => {
                        const presentation = getCategoryPresentation(
                          category.key,
                          category.type
                        );
                        const Icon = presentation.icon;
                        const isActive = selectedCategoryKey === category.key;
                        const isMeta = category.type === "meta";

                        const colors = COLOR_CLASSES[presentation.color];

                        return (
                          <button
                            key={category.key}
                            onClick={() => {
                              trackEvent("prompt_chip_clicked", {
                                chip_label: category.label,
                              });
                              if (isMeta) {
                                setInput(category.label);
                              } else {
                                setSelectedCategoryKey(
                                  isActive ? null : category.key
                                );
                              }
                            }}
                            disabled={hasIncompleteAttachments}
                            className={`
                          group flex items-center gap-2 px-3 py-2 rounded-full border text-xs whitespace-nowrap
                          transition-all duration-200 ease-in-out
                          border-gray-300 text-gray-600 dark:border-neutral-700 dark:text-neutral-300
                          ${
                            hasIncompleteAttachments
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer"
                          }
                          ${
                            isActive && !isMeta
                              ? `${colors.active} font-medium shadow-md shadow-gray-300/50 scale-105 dark:shadow-black/30`
                              : `bg-white ${colors.hover} hover:font-medium hover:shadow-md hover:shadow-gray-300/50 hover:scale-105 dark:bg-neutral-800 dark:hover:shadow-black/30`
                          }
                        `}
                            style={{
                              transition:
                                "transform 0.15s, box-shadow 0.2s, background 0.2s",
                            }}
                            data-testid={`suggested-prompt-type-${index}-button`}
                          >
                            <Icon className={`w-4 h-4 ${colors.icon}`} />
                            <span>{category.label}</span>
                            <ChevronRight
                              className={`w-4 h-4 transition-all duration-200 ${
                                isActive && !isMeta
                                  ? `rotate-90 ${colors.chevronActive}`
                                  : `text-gray-600 dark:text-neutral-300 ${colors.chevronHover}`
                              }`}
                            />
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Suggested Questions for Selected Quick Action */}
                  {showSelectedPrompts && (
                    <div className="flex flex-col items-center mt-4 gap-1 animate-fade-in w-full max-w-fit mx-auto">
                      {selectedCategory?.prompts.map((q, i) => (
                        <button
                          key={q}
                          onClick={() => setInput(q)}
                          disabled={hasIncompleteAttachments}
                          className={`
                            w-full text-left px-6 py-2 text-sm
                            bg-transparent border-none shadow-none
                            text-gray-500 dark:text-neutral-300
                            ${
                              hasIncompleteAttachments
                                ? "opacity-50 cursor-not-allowed"
                                : "cursor-pointer"
                            }
                            transition-colors
                            hover:text-gray-900 dark:hover:text-white
                            hover:no-underline
                          `}
                          data-testid={`suggested-prompt-${i}`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Disclaimer */}
              <div className="text-center py-4 px-6">
                <AIDisclaimer
                  variant="branded"
                  className="text-xs text-gray-500 dark:text-neutral-400"
                />
              </div>
            </div>
          </div>
        </div>
      </SidebarInset>
    </div>
  );
};

const GREETING_TEMPLATES = [
  "Where should we begin, {{username}}?",
  "Ready to dive in?",
  "What would you like to explore today?",
  "Let's make something happen, {{username}}.",
];

export const getGreetings = ({ username }: { username?: string }) => {
  const randomTemplate =
    GREETING_TEMPLATES[Math.floor(Math.random() * GREETING_TEMPLATES.length)];
  const userName = username ? username.split(" ")[0] : "there";
  return randomTemplate.replace(/{{username}}/g, userName);
};

export default Landing;
